import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState(null);
  const [series, setSeries]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [days, setDays]         = useState(30);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/tenant/analytics/overview'),
      api.get(`/tenant/analytics/api-calls?days=${days}`),
    ]).then(([o, s]) => { setOverview(o.data); setSeries(s.data); })
    .catch(err => {
      if (err.response?.status === 403) toast.error('Analytics requires Professional plan or higher');
      else toast.error(err.response?.data?.error || 'Failed to load analytics');
    })
    .finally(() => setLoading(false));
  }, [days]);

  const S = {
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 16, marginBottom: 24 },
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Loading analytics…</div>;
  if (!overview) return <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-3)', padding: 40 }}>Analytics unavailable on your current plan. Upgrade to Professional to unlock detailed reporting.</div>;

  const maxCalls   = Math.max(...series.map(d => d.total || 0), 1);
  const totalCalls = series.reduce((a, d) => a + (d.total || 0), 0);
  const totalErr   = series.reduce((a, d) => a + (d.errors || 0), 0);
  const avgResp    = series.length ? Math.round(series.reduce((a, d) => a + (d.avg_response_time || 0), 0) / series.length) : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Analytics</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border)', background: days === d ? 'var(--accent)' : 'transparent', color: days === d ? '#fff' : 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div style={S.grid}>
        {[
          { label: 'Endpoints',        val: overview.total_endpoints, icon: '⚡' },
          { label: `API Calls (${days}d)`, val: totalCalls.toLocaleString(), icon: '📡' },
          { label: 'Avg Response',     val: `${avgResp}ms`, icon: '⏱️' },
          { label: 'Success Rate',     val: totalCalls ? `${Math.round(((totalCalls - totalErr) / totalCalls) * 100)}%` : 'N/A', icon: '✅' },
          { label: 'Members',          val: overview.total_members, icon: '👥' },
          { label: 'Teams',            val: overview.total_teams, icon: '🏷️' },
        ].map(m => (
          <div key={m.label} style={S.card}>
            <div style={{ fontSize: 18, marginBottom: 6 }}>{m.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 2 }}>{m.val ?? '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{m.label}</div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>API Calls Over Time</h3>
        {series.length === 0 ? (
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No API call data for this period.</p>
        ) : (
          <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 120 }}>
            {series.map((d, i) => {
              const h = Math.round(((d.total || 0) / maxCalls) * 120);
              const errH = d.total ? Math.round((d.errors / d.total) * h) : 0;
              return (
                <div key={i} title={`${d.date}: ${d.total} calls, ${d.errors} errors`} style={{ flex: 1, minWidth: 3, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                  <div style={{ height: errH, background: '#f38ba8', borderRadius: '3px 3px 0 0' }} />
                  <div style={{ height: h - errH, background: 'var(--accent)', borderRadius: errH ? 0 : '3px 3px 0 0' }} />
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: 'var(--text-3)' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--accent)', borderRadius: 2, marginRight: 4 }} />Successful</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#f38ba8', borderRadius: 2, marginRight: 4 }} />Errors</span>
        </div>
      </div>
    </div>
  );
}
